/**
 * Formula engine — dependency graph builder + evaluation driver.
 *
 * Two entry points after hydrate:
 *   - `recomputeFormulas(sheet, range)` evaluates every formula row
 *     for every date in `range`. Called once at hydrate to fill
 *     initial derived values.
 *   - `recomputeDependents(sheet, changedRowId, range)` evaluates
 *     only the formula rows transitively downstream of the changed
 *     source row. Called after every user-facing cell / label /
 *     formula edit.
 *
 * Graph building happens via regex scan of the formula text — same
 * structural pass as `buildDependencyGraphFromRegistry` in lona-js.
 * The slim Lisp environment in `./model-formula.ts` handles actual
 * evaluation.
 *
 * Evaluation order: topological approximation via dependency-count
 * sort (matches `CellAccessor.evaluateFormulas` at
 * tento/tento-lona-js/src/cell-accessor.ts:545). For the small fixtures + the
 * CLI demo this is sufficient; proper topo sort can replace it
 * when multi-hop formula chains appear.
 */

import type { Option } from "../core/option.ts";
import { NaiveDate } from "@tento-chrono";
import {
  DependencyAspect,
  DependencyGraph,
  ReferenceType,
} from "@tento-core/dependency-graph";
import { Tracing } from "@tento-core/tracing";
import { Lisp } from "@tento-lisp/lisp";

import { type CellEntry, type CellValue, cellValueFromLisp } from "../core/dtype.ts";
import { evaluateFormula, ModelLispEnvironment } from "./model-formula.ts";
import {
  Actor,
  ColumnDataChanged,
  type Invalidation,
  RowHeightChanged,
  type RowLegendChanged,
  RowRangeCovered,
  RowRangeDataChanged,
  RowSyncStateChanged,
} from "../tx/invalidation.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { ModelSheet } from "../sheet/rows-sheet.ts";
import { sheetInternals, sheetStores } from "../sheet/sheet-stores.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { FormulaDataSource } from "../rows/variants/formula-data-source.ts";
import { SourceCells } from "../rows/variants/source-cells.ts";
import { SourceRow } from "../rows/variants/source-row.ts";
import { isCanonicalKey } from "../backends/canonical-backend.ts";
import { elementAt } from "../core/checked-index.ts";
import type { FormulaCoverage } from "./formula-coverage.ts";
import { sourceWriterOf } from "../rows/variants/source-row.ts";
import { CellId } from "../ids/ids.ts";

/**
 * Tag for engine-originated cell writes. Useful if a subscriber
 * wants to distinguish user edits from computed updates — today the
 * engine writes via `sourceCellData.set` which uses
 * `SourceRowCellData.ACTOR`; the sink consumer can inspect
 * `event.actor` to tell them apart if needed.
 */
export const FORMULA_ENGINE_ACTOR = new Actor("FormulaEngine");

const LABEL_REF_REGEX = /rows\["([^"]+)"\]/g;
const ID_REF_REGEX = /\br_([0-9a-f]{32})\b/gi;
/**
 * Multi-card cell read: `(cells "LABEL")` / `(cells "LABEL" D)`.
 * Dep tracking treats it the same as `rows["LABEL"]` — upstream
 * row changes invalidate the consumer.
 */
const CELLS_REF_REGEX = /\bcells\s+"([^"]+)"/g;

/**
 * Rebuild the sheet's dependency graph from scratch. Call at hydrate
 * + any time formula text / labels / lookup keys change for multiple
 * rows. For single-row formula edits use `rebindFormulaDataSource`.
 */
function buildDependencyGraph(sheet: ModelSheet): void {
  shapeMoved(sheet);
  const graph = sheetInternals(sheet).graph;
  for (const id of [...graph.nodes]) graph.removeNode(id);

  const rows = sheet.allRows();
  for (const row of rows) graph.addNode(row.id);
  const { byLabel, byLookupKey } = buildLabelMaps(sheet);

  for (const row of rows) {
    if (!(row instanceof FormulaDataSource)) continue;
    addEdgesForFormula(sheet, row.id, row.expression, byLabel, byLookupKey);
    addFormulaSourceEdge(sheet, row, byLabel, byLookupKey);
  }
}

/**
 * Rebuild a single formula row's outgoing edges. Used after
 * `FormulaDataSource.setExpression` to refresh dependencies without a full
 * graph rebuild.
 */
function rebindFormulaDataSource(sheet: ModelSheet, rowId: RowLocalId): void {
  shapeMoved(sheet);
  const graph = sheetInternals(sheet).graph;
  graph.clearDependencies(rowId);

  const row = sheet.peekRow(rowId);
  if (!(row instanceof FormulaDataSource)) return;

  const { byLabel, byLookupKey } = buildLabelMaps(sheet);
  addEdgesForFormula(sheet, rowId, row.expression, byLabel, byLookupKey);
  addFormulaSourceEdge(sheet, row, byLabel, byLookupKey);
}

/**
 * Build `byLabel` / `byLookupKey` maps used by formula-edge resolution.
 * SourceRow entries win over alias entries when both share a label —
 * formula references and `formulaSource` references want the
 * cell-bearing source, not the structural alias wrapper. Mirrors
 * `ops.ts:rowByLabel`'s disambiguation.
 */
function buildLabelMaps(
  sheet: ModelSheet,
): {
  byLabel: Map<string, RowLocalId>;
  byLookupKey: Map<string, RowLocalId>;
} {
  const byLabel = new Map<string, RowLocalId>();
  const byLookupKey = new Map<string, RowLocalId>();
  for (const row of sheet.allRows()) {
    for (const [literal, map] of [[row.label, byLabel], [row.lookupKey, byLookupKey]] as const) {
      if (literal == null) continue;
      const resolution = sheet.formulaReference(literal);
      if (resolution.kind === "resolved") map.set(literal, resolution.row.id);
    }
  }
  return { byLabel, byLookupKey };
}

function resolvedReference(sheet: ModelSheet, name: string): Option<ModelRow> {
  const resolution = sheet.formulaReference(name);
  return resolution.kind === "resolved" ? resolution.row : null;
}

function resolveReference(sheet: ModelSheet, name: string): Option<ModelRow> {
  const resolution = sheet.formulaReference(name);
  if (resolution.kind === "ambiguous") {
    throw new FormulaEngine.AmbiguousReference(name, resolution.candidates.map(row => String(row.id)));
  }
  return resolution.kind === "resolved" ? resolution.row : null;
}

export function findSourceDescendant(row: ModelRow): Option<SourceRow> {
  for (const child of row.children) {
    if (child instanceof SourceRow) return child;
    const nested = findSourceDescendant(child);
    if (nested !== null) return nested;
  }
  return null;
}

/**
 * Case 4: multi-card formula rows declare their iteration driver on
 * `descriptor.formulaSource`. That reference isn't in the formula
 * text, so `addEdgesForFormula` misses it. Add the edge explicitly
 * so source-row edits cascade to the formula row.
 */
function addFormulaSourceEdge(
  sheet: ModelSheet,
  row: FormulaDataSource,
  byLabel: Map<string, RowLocalId>,
  byLookupKey: Map<string, RowLocalId>,
): void {
  const desc = row.descriptor;
  if (desc.role !== "source") return;
  const ref = desc.formulaSource;
  if (!ref) return;
  const targetId = byLookupKey.get(ref) ?? byLabel.get(ref);
  if (!targetId) {
    sheetInternals(sheet).graph.trackUnresolved(ref, row.id);
    return;
  }
  sheetInternals(sheet).graph.addDependencyEdge(row.id, {
    targetRowId: targetId,
    referenceType: ReferenceType.LABEL,
    referenceLiteral: ref,
    aspects: new Set([DependencyAspect.VALUES]),
  });
}

function addEdgesForFormula(
  sheet: ModelSheet,
  rowId: RowLocalId,
  formula: string,
  byLabel: Map<string, RowLocalId>,
  byLookupKey: Map<string, RowLocalId>,
): void {
  if (!formula) return;
  const graph = sheetInternals(sheet).graph;
  const addNamedReference = (nameOrKey: string): void => {
    const targetId = byLookupKey.get(nameOrKey) ?? byLabel.get(nameOrKey);
    if (!targetId) {
      graph.trackUnresolved(nameOrKey, rowId);
      return;
    }
    graph.addDependencyEdge(rowId, {
      targetRowId: targetId,
      referenceType: ReferenceType.LABEL,
      referenceLiteral: nameOrKey,
      aspects: new Set([DependencyAspect.VALUES]),
    });
  };

  for (const match of formula.matchAll(LABEL_REF_REGEX)) {
    addNamedReference(elementAt(match, 1, "formula-engine: match"));
  }

  for (const match of formula.matchAll(CELLS_REF_REGEX)) {
    addNamedReference(elementAt(match, 1, "formula-engine: match"));
  }

  for (const match of formula.matchAll(ID_REF_REGEX)) {
    const target = sheet.peekRow(
      sheet.registry.resolveFromRawId(`r_${elementAt(match, 1, "formula-engine: match")}`),
    );
    if (target) {
      graph.addDependencyEdge(rowId, {
        targetRowId: target.id,
        referenceType: ReferenceType.ID,
        referenceLiteral: match[0],
        aspects: new Set([DependencyAspect.VALUES]),
      });
    } else {
      // Waits like a name does: an undo that re-adds the row binds it (`bindResolvedNames`).
      graph.trackUnresolved(match[0], rowId);
    }
  }
}

/**
 * Evaluate every `FormulaDataSource` in the sheet across `range` in
 * dependency order. Idempotent — safe to call repeatedly.
 *
 * Incremental: each formula row keeps the days it has already computed
 * against the current state of what it reads (`FormulaCoverage`), and only the
 * days of `range` it lacks are evaluated. A day leaves that coverage when a
 * cell it may read changes (any source cell on that day, or a cell of a row
 * the formula reads), when the row's expression or reference resolution
 * changes, or when a change cannot be placed. An evaluation that leaves the
 * stored value as it was writes nothing (no change event, no invalidation).
 * The cells this leaves in the store are the ones a full re-evaluation of
 * `range` leaves; the difference is only the work and the events.
 */
function recomputeFormulas(
  sheet: ModelSheet,
  range: NaiveDate.Range,
): void {
  const formulaRows = sheet.findByType(FormulaDataSource);
  if (formulaRows.length === 0) return;
  const coverage = refreshedCoverage(sheet);
  // One burst of writes (`CellDataStore.batch`): every computed cell still
  // emits its change, but a row cache mirroring the store notifies its
  // listeners once for the burst, not once per cell (dom-to-sdk-16: a scroll's
  // prefetch recompute fanned a formula row's listeners out per new column).
  inOneBurst(sheet, () => evaluateInOrder(sheet, coverage, formulaRows, range));
}

/** Run `fn`'s cell writes as one burst of the sheet's store (`CellDataStore.batch`). */
function inOneBurst(sheet: ModelSheet, fn: () => void): void {
  sheetStores(sheet).sourceCellData.batch(fn);
}

/**
 * Re-evaluate every formula row downstream of `changedRowId`. No-op
 * if nothing depends on the row.
 *
 * POLICY — eager transitive recompute. On a source-cell write, we
 * synchronously walk dependents and evaluate each formula row across
 * the write's range BEFORE `ColumnDataChanged` events propagate.
 * This guarantees facades never serve stale formula cells — an
 * observation the current facade dirty-flag pattern relies on (§5
 * no-stale-read invariant: formula recompute finishes inside `SourceRow.write`,
 * before `ColumnDataChanged` propagates).
 *
 * Cost is modest per-sheet (~1 ms per single-cell edit); escalates
 * only on full-range triggers (hydrate, setExpression, label-rename,
 * addRow). Each dependent evaluates only the days of `range` it has not
 * computed against the current state (see `recomputeFormulas`).
 *
 * Cross-sheet fanout under the canonical `SourceRow.CellDataStore`
 * would multiply by loaded-sheet count. Active-sheet gating
 * (`sheet.isActive`) is the planned mitigation — land alongside the
 * canonical store. Decision: stay eager within a sheet (cheap, and it
 * keeps the no-stale-read invariant); solve cross-sheet fanout at the
 * canonical cache, not here.
 */
function recomputeDependents(
  sheet: ModelSheet,
  changedRowId: RowLocalId,
  range: NaiveDate.Range,
): void {
  const dependents = DependencyGraph.dependentsOnly(sheetInternals(sheet).graph, changedRowId);
  if (dependents.size === 0) return;

  // Short-circuit: if no cell on `changedRowId` in `range` has been
  // modified since the last recompute, every dependent already ran
  // against the current upstream state — skip the whole pass.
  //
  // `set` / `clear` auto-mark stale; the post-pass `markFreshInRange`
  // below clears the bit. Callers that want to force a recompute can
  // `markStale` explicitly (e.g. after an attribute change that
  // didn't touch cell values). A formula row the engine left unwritten
  // on an unchanged day is stale where a full re-evaluation's write
  // would have made it so (`FormulaCoverage.wouldBeStale`).
  const cells = sheetStores(sheet).sourceCellData;
  const coverage = coverageOf(sheet);
  const changedRow = sheet.peekRow(changedRowId);
  const hasStaleCells = (changedRow instanceof SourceRow
    ? changedRow.anyStaleInRange(range)
    : cells.anyStaleInRange(changedRowId, range)) ||
    coverage.wouldBeStale(changedRowId, range.start.dse, range.end.dse);
  if (!hasStaleCells) return;

  const affected: FormulaDataSource[] = [];
  for (const id of dependents) {
    const row = sheet.peekRow(id);
    if (row instanceof FormulaDataSource) affected.push(row);
  }
  refreshedCoverage(sheet);
  evaluateInOrder(sheet, coverage, affected, range);

  // Consume staleness. Every pending change on `changedRowId` in
  // `range` has now been seen by every dependent — mark fresh so a
  // repeat call is a cheap no-op. Covers the post-clear case where
  // the cell is gone from `cells()` but the stale bit remains.
  if (changedRow instanceof SourceRow) {
    sourceWriterOf(changedRow).markFreshInRange(range);
  } else {
    cells.markFreshInRange(changedRowId, range);
  }
  coverage.consumeStale(changedRowId, range.start.dse, range.end.dse);
}

/**
 * Re-evaluate one formula row's cells across `range` and cascade to
 * every downstream formula. Used after `FormulaDataSource.setExpression`
 * mutates the expression — the row itself needs recomputing (its
 * old derived cells are stale) plus anything that depended on it. A new
 * expression is a new signature, so every day of `range` is evaluated.
 */
function recomputeRowAndDependents(
  sheet: ModelSheet,
  rowId: RowLocalId,
  range: NaiveDate.Range,
): void {
  // One burst of writes, as `recomputeFormulas`: a row's load evaluates its
  // whole range, and per-cell notifies fanned the row's listeners out once per cell.
  inOneBurst(sheet, () => {
    const row = sheet.peekRow(rowId);
    if (row instanceof FormulaDataSource) {
      const coverage = refreshedCoverage(sheet);
      evaluateInOrder(sheet, coverage, [row], range);
    }
    recomputeDependents(sheet, rowId, range);
  });
}

/**
 * Check whether assigning `formula` to `rowId` would introduce a
 * cycle in the sheet's dependency graph. Non-destructive: modifies
 * graph edges temporarily to remove the row's current outgoing
 * edges (so dep chains from the *old* formula don't flag false
 * positives), tests each proposed target, and restores the edges.
 *
 * Detects:
 *   - Self-references (`formula` pointing back at `rowId`).
 *   - Indirect cycles A → B → A, A → B → C → A, … — any chain
 *     where a proposed target `t` transitively depends on `rowId`,
 *     so adding the new edge `rowId → t` closes the loop.
 */
function wouldIntroduceCycle(
  sheet: ModelSheet,
  rowId: RowLocalId,
  formula: string,
): boolean {
  const graph = sheetInternals(sheet).graph;
  const targets = extractTargetIds(sheet, formula);

  const prevEdgeList = graph.edges.get(rowId);
  const snapshot = prevEdgeList ? [...prevEdgeList] : null;
  // `clearDependencies` also drops the row's waiting references
  // (`unresolvedNames`): restored with the edges, or a refused expression
  // would leave the kept one unable to bind when its input appears.
  const waitingOn = [...graph.unresolvedNames].flatMap(([name, rows]) => rows.has(rowId) ? [name] : []);
  graph.clearDependencies(rowId);
  try {
    for (const t of targets) {
      if (t === rowId) return true;
      // Walk dependencies forward from `t`. If we land on `rowId`
      // then `t → … → rowId` already exists and the new
      // `rowId → t` edge would close a cycle.
      if (reachableViaDependencies(graph, t, rowId)) return true;
    }
    return false;
  } finally {
    if (snapshot) for (const e of snapshot) graph.addDependencyEdge(rowId, e);
    for (const name of waitingOn) graph.trackUnresolved(name, rowId);
  }
}

/**
 * True iff `target` is reachable from `start` by following
 * `dependsOn` edges forward. A cycle-detection helper for
 * `wouldIntroduceCycle`.
 */
function reachableViaDependencies(
  graph: DependencyGraph<RowLocalId>,
  start: RowLocalId,
  target: RowLocalId,
): boolean {
  const stack: RowLocalId[] = [start];
  const visited = new Set<RowLocalId>();
  for (let node = stack.pop(); node !== undefined; node = stack.pop()) {
    if (node === target) return true;
    if (visited.has(node)) continue;
    visited.add(node);
    const deps = graph.dependsOn.get(node);
    if (deps) for (const d of deps) stack.push(d);
  }
  return false;
}

function extractTargetIds(
  sheet: ModelSheet,
  formula: string,
): readonly RowLocalId[] {
  const out: RowLocalId[] = [];
  for (const match of formula.matchAll(LABEL_REF_REGEX)) {
    const nameOrKey = elementAt(match, 1, "formula-engine: match");
    const target =
      resolvedReference(sheet, nameOrKey);
    if (target) out.push(target.id);
  }
  for (const match of formula.matchAll(CELLS_REF_REGEX)) {
    const nameOrKey = elementAt(match, 1, "formula-engine: match");
    const target =
      resolvedReference(sheet, nameOrKey);
    if (target) out.push(target.id);
  }
  for (const match of formula.matchAll(ID_REF_REGEX)) {
    const target = sheet.peekRow(
      sheet.registry.resolveFromRawId(`r_${elementAt(match, 1, "formula-engine: match")}`),
    );
    if (target) out.push(target.id);
  }
  return out;
}

/**
 * React to a `RowLegendChanged` event by rewriting every depender's
 * formula text that references the old label. The literal
 * `rows["<prev>"]` becomes `rows["<next>"]` in each dependent
 * FormulaDataSource, and the engine then rebinds + recomputes.
 *
 * No-op when `prev`/`next` are undefined (initial rename path) or
 * equal. If `next` is missing (label cleared) dependents are left
 * unresolved — the graph's `unresolvedNames` index tracks them so a
 * future rename can reconnect.
 */
function propagateLabelRename(
  sheet: ModelSheet,
  event: RowLegendChanged,
): void {
  // Called from the sheet's own event handlers, which run before the
  // coverage's subscription sees the event: say so here first.
  shapeMoved(sheet);
  const prev = event.prev;
  const next = event.next;
  if (prev === next) return;
  const graph = sheetInternals(sheet).graph;
  const affected = new Set<RowLocalId>();
  for (const name of [prev, next]) {
    if (name == null) continue;
    for (const id of graph.labelToDependers.get(name) ?? []) affected.add(id);
    for (const id of graph.unresolvedNames.get(name) ?? []) affected.add(id);
  }
  const renamed = sheet.peekRow(event.rowId);
  const target = renamed == null ? event.rowId : ((renamed instanceof SourceRow ? renamed : findSourceDescendant(renamed)) ?? renamed).id;
  for (const id of affected) {
    const row = sheet.peekRow(id);
    if (!(row instanceof FormulaDataSource)) continue;
    const bound = prev != null && (graph.edges.get(id) ?? []).some(edge => edge.referenceLiteral === prev && edge.targetRowId === target);
    if (bound && prev && next) {
      const formula = row.expression.replaceAll(`rows["${prev}"]`, `rows["${next}"]`).replaceAll(`cells "${prev}"`, `cells "${next}"`);
      if (formula !== row.expression) sheetStores(sheet).rowMetadata.patch(id, { formula });
    }
    rebindFormulaDataSource(sheet, id);
  }
  const range = observedRange(sheet);
  if (range) for (const id of affected) recomputeRowAndDependents(sheet, id, range);
}

/**
 * Hard cap on how far `observedRange` may extend from today, in days
 * (~20 years each way). The range is a RECOMPUTE WINDOW — every computed
 * row materializes cells across it, and `CalendarSystemRow` writes one
 * cell PER DAY — so its size is a direct multiplier on synchronous work.
 * A single far-future stored date makes it unbounded: an unbounded
 * recurring master's cell extent is deliberately stretched to the
 * server-side fetch sentinel 9999-12-31 (`recurring_master_horizon`,
 * `tento-lona-connector/src/cell.rs`) so range fetches always select the
 * master. That sentinel reaching this union turned a month→week switch
 * into a ~2.9-million-cell synchronous recompute — a permanent 100%-CPU
 * main-thread hang (measured live 2026-08-10 via Debugger.pause: the
 * spinning frame held `entries=Array(2912435)`, exactly today→9999 in
 * days). Clamping here fixes every consumer at once; the clamp is LOUD
 * (breadcrumb below) so legitimate far-window use is visible, and the
 * ingest-side fix (keep the sentinel out of client stores entirely) is
 * the named follow-up.
 */
const OBSERVED_RANGE_MAX_DAYS_FROM_TODAY = 7300;

/**
 * Client mirror of the server's recurring-master fetch sentinel
 * (`recurring_master_horizon`, `tento-lona-connector/src/cell.rs`:
 * 9999-12-31T00:00:00Z — both event-cell encoders share that one value, and
 * this mirror must not drift either). An unbounded recurring master's cell
 * extent is stretched to it so a date-range FETCH always selects the master;
 * it says nothing about where data lives. `observedRange` therefore treats a
 * timepoint end at/past this date as "unbounded" and takes only the START —
 * otherwise one master widens the recompute window to the full ±20y clamp
 * and every per-day computed row (`CalendarSystemRow`: one cell per day)
 * materializes ~7,500 cells on every month→week switch (measured 2026-08-10:
 * 599ms unthrottled, ~2.5s on a 4×-throttled laptop, on top of the original
 * unclamped 2.9M-day hang).
 */
const RECURRING_MASTER_HORIZON_DSE = NaiveDate.fromYmd1(9999, 12, 31).exp().dse;

/**
 * Union of every seeded-cell date across every source row on the
 * sheet, clamped to ±`OBSERVED_RANGE_MAX_DAYS_FROM_TODAY` around today.
 * Returns `null` when no cells exist yet — callers skip the recompute
 * entirely in that case — and also when every observed date falls
 * outside the clamp window (nothing recomputable in reach).
 */
function observedRange(sheet: ModelSheet): Option<NaiveDate.Range> {
  let minDse = Number.POSITIVE_INFINITY;
  let maxDse = Number.NEGATIVE_INFINITY;
  // Each row's store keeps its own extent (`RowCellData.dateExtent` /
  // `timepointExtent`), so this is O(rows) and copies no cell. The store is
  // the one `SourceRow.peekCells` reads: the client-wide canonical store for
  // a row with a canonical lookup key, the sheet's own otherwise.
  const stores = sheetStores(sheet);
  const canonical = sheetInternals(sheet).canonicalCells;
  for (const row of sheet.allRows()) {
    if (!(row instanceof SourceRow)) continue;
    const lookupKey = stores.rowMetadata.get(row.id).lookupKey;
    const data = isCanonicalKey(lookupKey) ? canonical.rowData(lookupKey) : stores.sourceCellData.rowData(row.id);
    if (data == null) continue;
    const dated = data.dateExtent();
    if (dated != null) {
      minDse = Math.min(minDse, dated.minDse);
      maxDse = Math.max(maxDse, dated.maxDse);
    }
    // Timepoint lane — timed events live there after the substrate
    // deconfliation; a date-lane-only scan would exclude their dates from the
    // recompute window, so formulas over timed events never fire. A
    // sentinel-stretched recurring master contributes only its start — see
    // `RECURRING_MASTER_HORIZON_DSE`. The ±clamp below stays as the
    // categorical backstop for any other extreme date.
    const timed = data.timepointExtent(RECURRING_MASTER_HORIZON_DSE);
    if (timed != null) {
      minDse = Math.min(minDse, timed.minDse);
      maxDse = Math.max(maxDse, timed.maxDse);
    }
  }
  if (!Number.isFinite(minDse)) return null;
  const todayDse = NaiveDate.today().dse;
  const loDse = todayDse - OBSERVED_RANGE_MAX_DAYS_FROM_TODAY;
  const hiDse = todayDse + OBSERVED_RANGE_MAX_DAYS_FROM_TODAY;
  const clampedMin = Math.max(minDse, loDse);
  const clampedMax = Math.min(maxDse, hiDse);
  if (clampedMin !== minDse || clampedMax !== maxDse) {
    // Loud, non-fatal: a stored date fell outside the recompute window —
    // either the recurring-master fetch sentinel (expected until the
    // ingest-side fix lands) or genuinely extreme user data.
    Tracing.log("formula/observed-range-clamped", {
      minDse,
      maxDse,
      clampedMin,
      clampedMax,
    });
  }
  if (clampedMin > clampedMax) return null;
  type Dse = Parameters<typeof NaiveDate.fromDse>[0];
  return new NaiveDate.Range(
    NaiveDate.fromDse(clampedMin as Dse),
    NaiveDate.fromDse(clampedMax as Dse),
  );
}

/**
 * A formula row whose references could not be bound when its edges were
 * built (`rows["Z"]` with no row labelled `Z`) waits in the graph's
 * `unresolvedNames`. When a row takes the label `name`, bind every such
 * formula to it and recompute it: its value now reads that row, and a loop
 * the binding closes is a cycle the next evaluation sees
 * (`formulaCycleRows`). Before, the name bound only at evaluation — no
 * edge, so neither the recompute cascade nor the cycle check saw it.
 */
function bindWaitingReferences(sheet: ModelSheet, name: string): void {
  const waiting = sheetInternals(sheet).graph.unresolvedNames.get(name);
  if (waiting == null || waiting.size === 0) return;
  const range = observedRange(sheet);
  for (const id of [...waiting]) {
    rebindFormulaDataSource(sheet, id);
    if (range) recomputeRowAndDependents(sheet, id, range);
  }
}

/**
 * The row a waiting reference `name` names now, or unset: a lookup key or a
 * label (the evaluation's own lookup), or — for an `r_<id>` reference — the
 * row with that id.
 */
function resolveWaiting(sheet: ModelSheet, name: string): Option<ModelRow> {
  const named = resolvedReference(sheet, name);
  if (named != null) return named;
  return ID_LITERAL_REGEX.test(name) ? sheet.peekRow(sheet.registry.resolveFromRawId(name)) : null;
}

const ID_LITERAL_REGEX = /^r_[0-9a-f]{32}$/i;

/**
 * Rows entered the sheet — added, restored, re-added by an undo (every
 * `NewRowInserted`; a batch's coalesced `SheetStructureChanged`): every
 * formula whose reference waits in `unresolvedNames` on a name that now
 * resolves — the new row's label, its lookup key, or its `r_<id>` — binds
 * and recomputes (`bindWaitingReferences`). Before, only a rename bound a
 * waiting reference: a formula added before its input, or whose input was
 * deleted and added again, showed no value until a reload (which resolves by
 * label at hydrate), so the live and the reloaded sheet disagreed.
 */
function bindResolvedNames(sheet: ModelSheet): void {
  // Called from the sheet's own event handlers, which run before the
  // coverage's subscription sees the event: say so here first.
  shapeMoved(sheet);
  const graph = sheetInternals(sheet).graph;
  const affected = new Set<RowLocalId>();
  for (const [name, readers] of graph.labelToDependers) {
    const resolution = sheet.formulaReference(name);
    for (const id of readers) {
      const edges = graph.edges.get(id) ?? [];
      if (resolution.kind !== "resolved" || edges.some(edge => edge.referenceLiteral === name && edge.targetRowId !== resolution.row.id)) affected.add(id);
    }
  }
  for (const [name, readers] of graph.unresolvedNames) {
    if (resolveWaiting(sheet, name) != null) for (const id of readers) affected.add(id);
  }
  if (affected.size === 0) return;
  for (const id of affected) rebindFormulaDataSource(sheet, id);
  const range = observedRange(sheet);
  if (range) for (const id of affected) recomputeRowAndDependents(sheet, id, range);
}

/**
 * Rows left the sheet (`Sheet.deleteRow`, after its teardown): every
 * surviving formula still bound to one of `deleted` rebinds — its reference
 * then waits in `unresolvedNames` (or binds to another row that has the name)
 * instead of keeping an edge to a dead id — and recomputes, so a row that
 * takes the name later binds it (`bindResolvedNames`).
 */
function releaseDeletedRows(sheet: ModelSheet, deleted: readonly RowLocalId[]): void {
  // Called from the sheet's own event handlers, which run before the
  // coverage's subscription sees the event: say so here first.
  shapeMoved(sheet);
  const gone = new Set(deleted);
  const bound: RowLocalId[] = [];
  for (const [from, edges] of sheetInternals(sheet).graph.edges) {
    if (!gone.has(from) && edges.some((edge) => gone.has(edge.targetRowId))) bound.push(from);
  }
  for (const rows of sheetInternals(sheet).graph.unresolvedNames.values()) {
    for (const id of rows) if (!gone.has(id) && !bound.includes(id)) bound.push(id);
  }
  if (bound.length === 0) return;
  const range = observedRange(sheet);
  for (const id of bound) {
    rebindFormulaDataSource(sheet, id);
    if (range) recomputeRowAndDependents(sheet, id, range);
  }
}

/**
 * A row gave up the label `name` (cleared, no new one): a formula bound to
 * it through `rows["name"]` now names no row. Rebind each (its reference
 * waits in `unresolvedNames`, so a later rename reconnects it) and
 * recompute it. Before, its edge to the row stayed, so a loop that edge
 * closed stayed a cycle (`formulaCycleRows`) with nothing left to read.
 */
function releaseReferences(sheet: ModelSheet, name: string): void {
  const dependers = sheetInternals(sheet).graph.labelToDependers.get(name);
  if (dependers == null || dependers.size === 0) return;
  const range = observedRange(sheet);
  for (const id of [...dependers]) {
    rebindFormulaDataSource(sheet, id);
    if (range) recomputeRowAndDependents(sheet, id, range);
  }
}

/**
 * The strongly-connected components of the dependency graph reachable from
 * `start` (Tarjan, iterative), dependencies first: every component comes
 * after the components it depends on. A component of more than one row, or
 * of one row that depends on itself, is a cycle.
 */
function componentsFrom(
  sheet: ModelSheet,
  start: readonly RowLocalId[],
): RowLocalId[][] {
  const deps = sheetInternals(sheet).graph.dependsOn;
  /** Per visited row: its visit index and the lowest index it reaches. */
  const seen = new Map<RowLocalId, { readonly index: number; low: number }>();
  const onStack = new Set<RowLocalId>();
  const stack: RowLocalId[] = [];
  const out: RowLocalId[][] = [];
  let next = 0;
  type Frame = { readonly id: RowLocalId; readonly mark: { readonly index: number; low: number }; readonly it: Iterator<RowLocalId> };
  for (const root of start) {
    if (seen.has(root)) continue;
    const frames: Frame[] = [];
    const enter = (id: RowLocalId): void => {
      const mark = { index: next, low: next };
      next++;
      seen.set(id, mark);
      stack.push(id);
      onStack.add(id);
      frames.push({ id, mark, it: (deps.get(id) ?? new Set<RowLocalId>()).values() });
    };
    enter(root);
    for (let frame = frames.at(-1); frame != null; frame = frames.at(-1)) {
      const step = frame.it.next();
      if (!step.done) {
        const dep = step.value;
        const depMark = seen.get(dep);
        if (depMark == null) enter(dep);
        else if (onStack.has(dep)) frame.mark.low = Math.min(frame.mark.low, depMark.index);
        continue;
      }
      frames.pop();
      const parent = frames.at(-1);
      if (parent != null) parent.mark.low = Math.min(parent.mark.low, frame.mark.low);
      if (frame.mark.low === frame.mark.index) {
        const component: RowLocalId[] = [];
        for (let id = stack.pop(); id !== undefined; id = stack.pop()) {
          onStack.delete(id);
          component.push(id);
          if (id === frame.id) break;
        }
        out.push(component);
      }
    }
  }
  return out;
}

/** True iff `component` (from `componentsFrom`) is a dependency cycle. */
function isCycle(sheet: ModelSheet, component: readonly RowLocalId[]): boolean {
  if (component.length > 1) return true;
  const only = component[0];
  return only != null && (sheetInternals(sheet).graph.dependsOn.get(only)?.has(only) ?? false);
}

/**
 * Every row of `sheet` that sits in a formula dependency cycle — a formula
 * that reads itself, directly or through other rows. The engine never
 * evaluates such a row: it holds no cells (`evaluateInOrder`). A cycle
 * `setExpression` would close is refused before it forms
 * (`wouldIntroduceCycle`); one stored on the server, or closed when a rename
 * binds a waiting reference (`bindWaitingReferences`), is answered here.
 */
export function formulaCycleRows(sheet: ModelSheet): ReadonlySet<RowLocalId> {
  const cyclic = new Set<RowLocalId>();
  const start = sheet.findByType(FormulaDataSource).map((row) => row.id);
  for (const component of componentsFrom(sheet, start)) {
    if (isCycle(sheet, component)) for (const id of component) cyclic.add(id);
  }
  return cyclic;
}

/**
 * Evaluate `rows` across `range` in dependency order (a row after every row
 * it reads), each only on the days it has not computed against the current
 * state (`evaluateRowMissing`). A row in a cycle is not evaluated: its cells
 * in `range` are cleared — a cycle has no value, and the numbers an
 * evaluation in some order would leave behind look plausible — its coverage
 * is dropped (so the pass after the cycle opens evaluates, and writes, every
 * day), and a `formula-engine/cycle` breadcrumb names it.
 */
function evaluateInOrder(
  sheet: ModelSheet,
  coverage: FormulaCoverage,
  rows: readonly FormulaDataSource[],
  range: NaiveDate.Range,
): void {
  if (rows.length === 0) return;
  const wanted = new Map<RowLocalId, FormulaDataSource>();
  for (const row of rows) wanted.set(row.id, row);
  for (const component of componentsFrom(sheet, [...wanted.keys()])) {
    const cycle = isCycle(sheet, component);
    for (const id of component) {
      const row = wanted.get(id);
      if (row == null) continue;
      if (cycle) clearCyclicRow(sheet, coverage, row, component, range);
      else evaluateRowMissing(sheet, coverage, row, range);
    }
  }
}

function clearCyclicRow(
  sheet: ModelSheet,
  coverage: FormulaCoverage,
  row: FormulaDataSource,
  component: readonly RowLocalId[],
  range: NaiveDate.Range,
): void {
  Tracing.log("formula-engine/cycle", {
    row: String(row.id),
    label: row.label ?? null,
    cycle: component.map(String),
  });
  const store = sheetStores(sheet).sourceCellData;
  for (const entry of store.cells(row.id, range)) {
    store.clear(row.id, entry.date, entry.id ?? null, null);
  }
  // Whatever the row computed before it closed the loop no longer holds.
  coverage.uncover(row.id);
}

/**
 * The configuration error an evaluation of `row` throws before evaluating
 * any day, or unset: a multi-card formula row must declare `formulaSource`.
 * Falling through to the single-card path would silently drop the lambda
 * body (it would evaluate to a bare function and evaluateFormula returns
 * null). Surfacing the config error loudly at evaluation time beats empty
 * cells with no diagnosis. Per formula-typed-outputs.md §2.3 and §5.2.
 */
function evaluationConfigError(row: FormulaDataSource): Option<Error> {
  if (!row.expression) return null;
  const desc = row.descriptor;
  if (desc.role === "source" && desc.cellCardinality === "multi" && !desc.formulaSource) {
    return new Error(
      `FormulaDataSource ${row.id}: multi-card formula rows must ` +
        `declare \`formulaSource\` on their descriptor attributes; ` +
        `got cellCardinality="multi" with no source.`,
    );
  }
  return null;
}

/**
 * Evaluate `row` on the days `[fromDse, toDse)`; `skipEqual`: leave a day
 * whose computed value (and style) is the stored one untouched. The row's own
 * writes are marked as the engine's (`evaluating`), so they invalidate the
 * rows that read it, not itself. Answers the days whose evaluation threw.
 */
function evaluateRowDays(
  sheet: ModelSheet,
  coverage: FormulaCoverage,
  row: FormulaDataSource,
  fromDse: number,
  toDse: number,
  skipEqual: boolean,
): number[] {
  const outer = coverage.evaluating;
  coverage.evaluating = row.id;
  const threw: number[] = [];
  try {
    coverage.evaluations += evaluateRowDaysUnmarked(sheet, row, fromDse, toDse, skipEqual, threw);
  } finally {
    coverage.evaluating = outer;
  }
  return threw;
}

/** Evaluate `row` on `[fromDse, toDse)`; answers how many days it evaluated. */
function evaluateRowDaysUnmarked(
  sheet: ModelSheet,
  row: FormulaDataSource,
  fromDse: number,
  toDse: number,
  skipEqual: boolean,
  threw: number[],
): number {
  const formula = row.expression;
  if (!formula) return 0;

  const desc = row.descriptor;
  const isMultiCard = desc.role === "source" && desc.cellCardinality === "multi";

  type Dse = Parameters<typeof NaiveDate.fromDse>[0];
  let evaluated = 0;
  for (let dse = fromDse; dse < toDse; dse++) {
    evaluated++;
    const date = NaiveDate.Partial.fromDateAndType(
      "day",
      NaiveDate.fromDse(dse as Dse),
    );
    try {
      if (isMultiCard && desc.role === "source" && desc.formulaSource) {
        evaluateCase4AtDate(sheet, row, desc.formulaSource, date);
      } else {
        evaluateSingleCardAtDate(sheet, row, formula, date, skipEqual);
      }
    } catch (err) {
      threw.push(dse);
      // A day whose evaluation fails has no value — what a reload computes
      // (hydrate writes nothing for it). Before, the day kept the value of
      // its last good evaluation: a formula whose input row was deleted, or
      // whose new expression names a row that does not exist, went on
      // showing a number the reloaded sheet does not.
      clearDay(sheet, row, date);
      const source = desc.role === "source" && desc.formulaSource
        ? desc.formulaSource
        : formula;
      Tracing.log("formula-engine/evaluation-failed", {
        row: String(row.id),
        date: date.start.toString(),
        formula: source,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
  return evaluated;
}

/** Clear every stored output of formula row `row` on `date` (single- and multi-card). */
function clearDay(sheet: ModelSheet, row: FormulaDataSource, date: NaiveDate.Partial): void {
  const store = sheetStores(sheet).sourceCellData;
  for (const entry of store.cells(row.id, date.toRange())) {
    if (entry.date.type !== date.type || entry.date.index !== date.index) continue;
    store.clear(row.id, date, entry.id ?? null, null);
  }
}

function evaluateSingleCardAtDate(
  sheet: ModelSheet,
  row: FormulaDataSource,
  formula: string,
  date: NaiveDate.Partial,
  skipEqual: boolean,
): void {
  const ctx = {
    sheet,
    row,
    column: date,
    style: new Map<string, string>(),
    tz: null,
    dbg: null,
  };
  const evaluated = evaluateFormula(formula, ctx);
  const result = evaluated == null ? null : cellValueFromLisp(evaluated);
  const stored = skipEqual ? storedDayEntry(sheet, row, date) : null;
  const store = sheetStores(sheet).sourceCellData;
  if (result === null) {
    if (skipEqual && stored === null) return;
    store.clear(row.id, date, null, null);
    return;
  }
  // Snapshot the style map populated by `cell.bg` (and friends)
  // during evaluation. Undefined when no style calls fired —
  // keeps CellEntry.style out of the entry for the common case.
  const style = ctx.style.size > 0 ? new Map(ctx.style) : undefined;
  if (
    stored !== null &&
    sameJsonValue(stored.value, result) &&
    sameStyle(stored.style, style ?? null)
  ) {
    return;
  }
  store.set(
    row.id,
    date,
    result,
    null,
    style ?? null,
    null,
  );
}

/**
 * Case 4 (multi → multi per-cell map): engine-owned iteration over
 * the source row's cells at `date`, applying the formula as a
 * lambda per input cell. Output cellId is the lambda return's `.id`
 * if set, else the input cell's id (fallback).
 *
 * Clearing semantics: before writing, every existing output cell at
 * `date` whose input is no longer present is cleared. This keeps
 * invalidation precise — per-cell fall-out from the engine-owned
 * iteration (no formula-body analysis needed).
 */
function evaluateCase4AtDate(
  sheet: ModelSheet,
  row: FormulaDataSource,
  sourceLabel: string,
  date: NaiveDate.Partial,
): void {
  const candidate =
    resolveReference(sheet, sourceLabel);
  if (!candidate) return;
  // `formulaSource` may name an alias whose source descendant carries
  // the cells (calendar template: alias label="Cal", descendant
  // data-source label="Calendar"). Walk down to the source so the
  // iteration sees the cell-bearing row, not the structural wrapper.
  const src = candidate instanceof SourceRow
    ? candidate
    : findSourceDescendant(candidate);
  if (src == null) return;

  const expr = Lisp.unsafeParse(row.expression);
  // Shared style bag: cleared before each lambda invocation so
  // cells get independent styling. The env captures this map by
  // reference when `cell.bg` resolves, so clearing in place
  // propagates — no env rebuild needed per cell.
  const perCellStyle = new Map<string, string>();
  const env = new ModelLispEnvironment(new Map(), {
    sheet,
    row,
    column: date,
    style: perCellStyle,
    tz: null,
    dbg: null,
  });
  const lambda = Lisp.evalThrows(expr, env);
  if (typeof lambda !== "function") {
    throw new Error(
      `FormulaDataSource ${row.id}: multi-card formula must evaluate to a lambda; got ${typeof lambda}`,
    );
  }

  // Collect input cells at this date AND snapshot the current output
  // keys so we can clear the ones that no longer have an input.
  // One day's read (both lanes: the date lane by start day, the timepoint
  // lane by UTC day), not every cell the source holds.
  const inputCells: CellEntry[] = [];
  const srcCells = src.peekCells(date.toRange());
  for (const entry of SourceCells.dates(srcCells)) {
    if (entry.date.type !== date.type) continue;
    if (entry.date.index !== date.index) continue;
    inputCells.push(entry);
  }
  // Timepoint lane — timed events live there after the substrate
  // deconfliation; project each to a day-keyed `CellEntry` so the per-date
  // filter matches, otherwise a formula over an upstream calendar drops every
  // timed event.
  for (const tp of SourceCells.customs(srcCells)) {
    // Match the eval date's construction (`fromDateAndType("day", …)`) so the
    // per-date filter compares like for like — a bare `fromDate` can yield a
    // different partial `type` and skip every timed event.
    const d = NaiveDate.Partial.fromDateAndType("day", tp.dtr.start.date);
    if (d.type !== date.type || d.index !== date.index) continue;
    inputCells.push(
      { date: d, id: tp.id, value: tp.value, style: tp.style },
    );
  }
  const survivingIds = new Set<string>();

  for (const cell of inputCells) {
    perCellStyle.clear();
    const out = lambda([cell.value as Lisp.Atom], env);
    if (out === null || out === undefined) continue;
    // Defensive: a lambda body that returns a bare function produces
    // nothing meaningful to a cell reader and would leak the Lisp
    // procedure into the store. Mirrors `evaluateFormula`'s drop on
    // the single-card path.
    if (typeof out === "function") continue;
    const cellId = extractCellId(out) ?? cell.id;
    if (cellId == null) continue;
    survivingIds.add(cellId);
    // Per-cell invalidation: skip the write (and its event) when
    // the computed output hasn't changed. Case 4's defining
    // property — only cells whose input changed emit a downstream
    // event.
    const prev = sheetStores(sheet).sourceCellData.get(row.id, date, CellId.of(cellId));
    if (cellValueEquals(prev, out)) continue;
    const style = perCellStyle.size > 0 ? new Map(perCellStyle) : undefined;
    sheetStores(sheet).sourceCellData.set(
      row.id,
      date,
      out,
      CellId.of(cellId),
      style ?? null,
      null,
    );
  }

  // Clear stale output cells at this date — inputs that existed on a
  // prior evaluation but don't now. Per-cell invalidation: only the
  // specific stale cellIds are touched.
  for (const entry of sheetStores(sheet).sourceCellData.cells(row.id, date.toRange())) {
    if (entry.date.type !== date.type) continue;
    if (entry.date.index !== date.index) continue;
    if (entry.id != null && !survivingIds.has(entry.id)) {
      sheetStores(sheet).sourceCellData.clear(row.id, date, entry.id, null);
    }
  }
}

function extractCellId(value: unknown): Option<string> {
  if (value && typeof value === "object" && !Array.isArray(value) && "id" in value) {
    const id = value.id;
    if (typeof id === "string") return id;
  }
  return null;
}

/**
 * Structural equality for cell values. Scalars compare by `===`;
 * objects by shallow key-by-key equality (Case 4 outputs are
 * one-level-deep objects — `{id, title, category, …}`). Good enough
 * to gate per-cell invalidation without a JSON.stringify round-trip.
 */
function cellValueEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!cellValueEquals(a[i], b[i])) return false;
    }
    return true;
  }
  const keysA = Object.keys(a as object);
  const keysB = Object.keys(b as object);
  if (keysA.length !== keysB.length) return false;
  for (const k of keysA) {
    if (
      !cellValueEquals(
        (a as Record<string, unknown>)[k],
        (b as Record<string, unknown>)[k],
      )
    ) {
      return false;
    }
  }
  return true;
}

/**
 * The stored single-cardinality `day` entry of formula row `row` at `date`
 * (the entry `evaluateSingleCardAtDate` writes), or unset.
 */
function storedDayEntry(
  sheet: ModelSheet,
  row: FormulaDataSource,
  date: NaiveDate.Partial,
): Option<CellEntry> {
  for (const entry of sheetStores(sheet).sourceCellData.cells(row.id, date.toRange())) {
    if (entry.id == null && entry.date.type === date.type && entry.date.index === date.index) {
      return entry;
    }
  }
  return null;
}

/**
 * Whether a stored cell value and a freshly computed one are the same value:
 * primitives by `===`, arrays element-wise, plain objects key-wise. Any other
 * object (a class instance, whose state may live in private fields) is the
 * same only as the same object, so a computed instance is always written.
 */
function sameJsonValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!sameJsonValue(a[i], b[i])) return false;
    }
    return true;
  }
  if (!isPlainObject(a) || !isPlainObject(b)) return false;
  const keysA = Object.keys(a);
  if (keysA.length !== Object.keys(b).length) return false;
  for (const k of keysA) {
    if (!Object.hasOwn(b, k) || !sameJsonValue(a[k], b[k])) return false;
  }
  return true;
}

function isPlainObject(v: object): v is Record<string, unknown> {
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

function sameStyle(a: Option<ReadonlyMap<string, string>>, b: Option<ReadonlyMap<string, string>>): boolean {
  if (a === b) return true;
  if (a === null || b === null || a.size !== b.size) return false;
  for (const [k, v] of a) {
    if (b.get(k) !== v) return false;
  }
  return true;
}

// ── Incremental recompute ─────────────────────────────────────────────

/**
 * The sheet's coverage, watching its two cell stores and its shape (every
 * invalidation but a cell-value one; `FormulaCoverage`'s module notes) from
 * the first use on.
 */
function coverageOf(sheet: ModelSheet): FormulaCoverage {
  const internals = sheetInternals(sheet);
  const coverage = internals.formulaCoverage;
  coverage.attach(
    sheetStores(sheet).sourceCellData,
    internals.canonicalCells,
    (moved) =>
      sheet.invalidationSink.subscribe((event) => {
        if (!cellValueOnly(event)) moved();
      }),
  );
  return coverage;
}

/**
 * Invalidations that announce cell values, sync state or row heights only —
 * nothing a formula signature reads (`readSignature`: names, ids, classes,
 * children, descriptors, expressions). Every other invalidation moves the
 * coverage's shape. A cell-value event is the one a scroll's loads emit, so a
 * scroll refreshes no signature.
 */
function cellValueOnly(event: Invalidation): boolean {
  return event instanceof ColumnDataChanged ||
    event instanceof RowRangeDataChanged ||
    event instanceof RowRangeCovered ||
    event instanceof RowSyncStateChanged ||
    event instanceof RowHeightChanged;
}

/** Something a signature reads moved outside the sheet's events (the engine's own graph edits). */
function shapeMoved(sheet: ModelSheet): void {
  sheetInternals(sheet).formulaCoverage.shapeMoved();
}

/** `(cells "LABEL")` with a literal label; any other use of `cells` is dynamic. */
const CELLS_DYNAMIC_REGEX = /\bcells\b(?!\s+")/;

/**
 * A formula row's reads as its evaluation resolves them now: every row
 * reference (`rows["…"]`, `(cells "…")`, `r_<id>`, a multi-card
 * `formulaSource`) through the environment's own lookup, to the cell-bearing
 * row whose cells are read, with that row's store key and cardinality.
 */
function readSignature(
  sheet: ModelSheet,
  row: FormulaDataSource,
): { signature: string; reads: Option<ReadonlySet<RowLocalId>> } {
  const formula = row.expression;
  const desc = row.descriptor;
  const parts: string[] = [formula];
  const reads = new Set<RowLocalId>();
  const resolved = (target: Option<ModelRow>): void => {
    if (target == null) {
      parts.push("-");
      return;
    }
    const source = target instanceof SourceRow ? target : findCellBearing(target);
    if (source == null) {
      parts.push(`${String(target.id)}:-`);
      return;
    }
    reads.add(source.id);
    parts.push(
      `${String(target.id)}:${String(source.id)}:${source.lookupKey ?? ""}:` +
        (source.descriptor.role === "source" ? source.descriptor.cellCardinality : ""),
    );
  };
  const byName = (name: string): void => {
    const resolution = sheet.formulaReference(name);
    if (resolution.kind === "ambiguous") parts.push(`?${resolution.candidates.map(row => String(row.id)).join(",")}`);
    resolved(resolution.kind === "resolved" ? resolution.row : null);
  };
  if (formula) {
    for (const match of formula.matchAll(LABEL_REF_REGEX)) byName(elementAt(match, 1, "formula-engine: match"));
    for (const match of formula.matchAll(CELLS_REF_REGEX)) byName(elementAt(match, 1, "formula-engine: match"));
    for (const match of formula.matchAll(ID_REF_REGEX)) {
      resolved(sheet.peekRow(sheet.registry.resolveFromRawId(`r_${elementAt(match, 1, "formula-engine: match")}`)));
    }
  }
  if (desc.role === "source") {
    parts.push(`#${desc.cellCardinality}:${desc.formulaSource ?? ""}`);
    if (desc.formulaSource) {
      const candidate = resolvedReference(sheet, desc.formulaSource);
      const src = candidate == null
        ? null
        : candidate instanceof SourceRow
        ? candidate
        : findSourceDescendant(candidate);
      parts.push(src == null ? "~-" : `~${String(src.id)}`);
      if (src != null) reads.add(src.id);
    }
  }
  const dynamic = formula ? CELLS_DYNAMIC_REGEX.test(formula) : false;
  return { signature: parts.join("\u0000"), reads: dynamic ? null : reads };
}

/** The first cell-bearing row under `row`, depth-first (the formula runtime's walk). */
function findCellBearing(row: ModelRow): Option<SourceRow> {
  for (const child of row.children) {
    if (child instanceof SourceRow) return child;
    const nested = findCellBearing(child);
    if (nested !== null) return nested;
  }
  return null;
}

/**
 * The sheet's coverage, with every formula row's current reads recorded:
 * read again only when the sheet's shape moved since the last refresh
 * (`FormulaCoverage.current`) — once per formula row per shape change, never
 * per load.
 */
function refreshedCoverage(sheet: ModelSheet): FormulaCoverage {
  const coverage = coverageOf(sheet);
  if (coverage.current) return coverage;
  const shape = coverage.shape;
  const formulaRows = sheet.findByType(FormulaDataSource);
  coverage.refresh(new Map(formulaRows.map((row) => [row.id, readSignature(sheet, row)])), shape);
  return coverage;
}

/**
 * Evaluate the days of `range` `row` has not computed against the current
 * state, then cover `range`. The cells this leaves are the ones evaluating
 * every day of `range` leaves.
 */
function evaluateRowMissing(
  sheet: ModelSheet,
  coverage: FormulaCoverage,
  row: FormulaDataSource,
  range: NaiveDate.Range,
): void {
  const configError = evaluationConfigError(row);
  if (configError !== null) throw configError;
  const lo = range.start.dse;
  const hi = range.end.dse;
  const missing = coverage.missing(row.id, lo, hi);
  const skipEqual = !coverage.takeRewrite(row.id);
  for (const [from, to] of missing) {
    coverage.evaluated(row.id, from, to, evaluateRowDays(sheet, coverage, row, from, to, skipEqual));
  }
  // A change queued meanwhile is applied before the next row evaluates, and
  // so after this cover: it is never masked.
  coverage.cover(row.id, lo, hi);
  // A single-card evaluation of every day would have written (and marked
  // stale) each day that did not throw; a multi-card one writes only what
  // changed, which this pass wrote too.
  const desc = row.descriptor;
  const multi = desc.role === "source" && desc.cellCardinality === "multi";
  if (row.expression && !multi) coverage.passedWritingAll(row.id, lo, hi);
}

/**
 * How many (formula row, day) evaluations the engine has run on `sheet` —
 * the complexity counter the recompute regression tests pin. Not on a
 * barrel: tests read it by module path.
 */
export function formulaEvaluationCount(sheet: ModelSheet): number {
  return sheetInternals(sheet).formulaCoverage.evaluations;
}

/**
 * How many formula-row signatures the engine has read on `sheet` (one per
 * formula row per shape change; none per load) — the counter the signature
 * tests pin. Not on a barrel: tests read it by module path.
 */
export function formulaSignatureBuildCount(sheet: ModelSheet): number {
  return sheetInternals(sheet).formulaCoverage.signatureBuilds;
}

/** Copy the model-owned graph and waiting references into an SDK read/cache projection. */
export function copyFormulaGraph(sheet: ModelSheet, destination: {
  readonly nodes: ReadonlySet<RowLocalId>;
  removeNode(id: RowLocalId): void;
  addNode(id: RowLocalId): void;
  addDependencyEdge(id: RowLocalId, edge: {
    targetRowId: RowLocalId;
    referenceType: ReferenceType;
    referenceLiteral: Option<string>;
    aspects: Set<DependencyAspect>;
  }): void;
  trackUnresolved(name: string, id: RowLocalId): void;
}): void {
  const source = sheetInternals(sheet).graph;
  for (const id of [...destination.nodes]) destination.removeNode(id);
  for (const id of source.nodes) destination.addNode(id);
  for (const [id, edges] of source.edges) {
    for (const edge of edges) destination.addDependencyEdge(id, { ...edge, referenceLiteral: edge.referenceLiteral ?? null, aspects: new Set(edge.aspects) });
  }
  for (const [name, readers] of source.unresolvedNames) {
    for (const id of readers) destination.trackUnresolved(name, id);
  }
}


const $resolveReference = resolveReference;
const $buildDependencyGraph = buildDependencyGraph;
const $rebindFormulaDataSource = rebindFormulaDataSource;
const $recomputeFormulas = recomputeFormulas;
const $recomputeDependents = recomputeDependents;
const $recomputeRowAndDependents = recomputeRowAndDependents;
const $wouldIntroduceCycle = wouldIntroduceCycle;
const $extractTargetIds = extractTargetIds;
const $propagateLabelRename = propagateLabelRename;
const $observedRange = observedRange;
const $bindResolvedNames = bindResolvedNames;
const $releaseDeletedRows = releaseDeletedRows;

/**
 * The formula engine's entry points (dependency graph, rebinding, recompute), which the SDK calls
 * after hydrating a sheet.
 */
export namespace FormulaEngine {
  /** A formula name identifies multiple distinct inputs; no insertion-order value is current. */
  export class AmbiguousReference extends Error {
    /** The literal the formula reads. */
    readonly reference: string;
    /** Stable source locators, sorted independently of row insertion order. */
    readonly candidates: readonly string[];
    /** Construct the diagnostic for an ambiguous formula input. */
    constructor(reference: string, candidates: readonly string[]) {
      super(`Ambiguous formula reference "${reference}": ${candidates.join(", ")}`);
      this.name = "AmbiguousFormulaReferenceError";
      this.reference = reference;
      this.candidates = Object.freeze([...candidates]);
    }
  }

  /** Resolve a name with the sheet's shared formula policy; ambiguous inputs throw with their candidate ids. */
  export const resolveReference: (sheet: ModelSheet, name: string) => Option<ModelRow> = $resolveReference;
  export const buildDependencyGraph: (sheet: ModelSheet) => void = $buildDependencyGraph;
  export const rebindFormulaDataSource: (sheet: ModelSheet, rowId: RowLocalId) => void = $rebindFormulaDataSource;
  export const recomputeFormulas: (sheet: ModelSheet, range: NaiveDate.Range) => void = $recomputeFormulas;
  export const recomputeDependents: (sheet: ModelSheet, changedRowId: RowLocalId, range: NaiveDate.Range) => void = $recomputeDependents;
  export const recomputeRowAndDependents: (sheet: ModelSheet, rowId: RowLocalId, range: NaiveDate.Range) => void = $recomputeRowAndDependents;
  export const wouldIntroduceCycle: (sheet: ModelSheet, rowId: RowLocalId, formula: string) => boolean = $wouldIntroduceCycle;
  export const extractTargetIds: (sheet: ModelSheet, formula: string) => readonly RowLocalId[] = $extractTargetIds;
  export const propagateLabelRename: (sheet: ModelSheet, event: RowLegendChanged) => void = $propagateLabelRename;
  export const observedRange: (sheet: ModelSheet) => Option<NaiveDate.Range> = $observedRange;
  /** Rows entered `sheet`: bind (and recompute) every waiting formula reference a row now resolves. */
  export const bindResolvedNames: (sheet: ModelSheet) => void = $bindResolvedNames;
  /** Rows left `sheet`: the formulas bound to `deleted` rebind, so their references wait for the name again. */
  export const releaseDeletedRows: (sheet: ModelSheet, deleted: readonly RowLocalId[]) => void = $releaseDeletedRows;
}
